import fp from "lodash/fp"

import type {
  PositionFieldMapping,
  PositionSummaryValues,
} from "types/devicePosition.types"
import type { EntityTelemetry } from "types/telemetries.types"
import { MOTION_ALPHA_FIELD, MOTION_BETA_FIELD } from "helpers/config/telemetryFieldDefs"

const defaultPositionFieldMapping: PositionFieldMapping = {
  first: MOTION_ALPHA_FIELD,
  second: MOTION_BETA_FIELD,
}

const toMillis = (date: string) => new Date(date).getTime()
const getTime = (o: EntityTelemetry) => toMillis(o._time)

export const getLatestAngles = (
  data: EntityTelemetry[] | undefined,
  fieldMapping: PositionFieldMapping = defaultPositionFieldMapping,
): PositionSummaryValues => {
  type MaybePartialValues = Partial<PositionSummaryValues> | null

  // Pivot data and convert time to number
  type _Mangle = (o: EntityTelemetry, valueKey: string) => Partial<PositionSummaryValues>
  const _mangle: _Mangle = (o, valueKey) => ({
    ...o,
    [valueKey]: o._value,
    _time: toMillis(o._time),
  })

  // Latest for field, mangle the result to form the position plot values
  type _LatestPivot = (
    field: string,
    pivotKey: string,
    byField: Record<string, EntityTelemetry[]>,
  ) => MaybePartialValues
  const _latestPivot: _LatestPivot = (field, pivotKey, byField) => {
    const telemetries = fp.getOr([], field, byField)
    const latest = fp.maxBy(getTime, telemetries)
    return latest ? _mangle(latest, pivotKey) : null
  }

  // Merge the partial values, use max time
  type _Merge = (
    sources: MaybePartialValues[],
    fallback: PositionSummaryValues,
  ) => PositionSummaryValues
  const _merge: _Merge = (sources, fallback) => {
    return fp.mergeAllWith(
      (a, b, key) => {
        if (key === "_time") return fp.max([a, b])
      },
      [fallback, ...fp.filter((o) => Boolean(o), sources)],
    )
  }

  const byField = fp.groupBy((o: EntityTelemetry) => o._field, data)

  const latestFirst = _latestPivot(fieldMapping.first, "first", byField)
  const latestSecond = _latestPivot(fieldMapping.second, "second", byField)

  // The position plot doesn't deal with missing data nicely, so add a fallback
  const fallback: PositionSummaryValues = {
    _time: Number.NEGATIVE_INFINITY,
    first: 0.0,
    second: 0.0,
  }

  return _merge([latestFirst, latestSecond], fallback)
}

/** Drift = last sample − first sample for a single field, or undefined when
 *  the field has no data. */
export const getFieldDrift = (
  data: EntityTelemetry[] | undefined,
  field: string,
): number | undefined => {
  const telemetries = fp.filter({ _field: field }, data ?? [])
  if (telemetries.length === 0) return undefined
  const sorted = fp.sortBy(getTime, telemetries)
  return Number(sorted[sorted.length - 1]._value) - Number(sorted[0]._value)
}

export const getAnglesDrift = (
  data: EntityTelemetry[] | undefined,
  fieldMapping: PositionFieldMapping = defaultPositionFieldMapping,
) => ({
  first: { _value: getFieldDrift(data, fieldMapping.first) },
  second: { _value: getFieldDrift(data, fieldMapping.second) },
})

// Relative-heading sector keys, ordered from front (0°) counter-clockwise.
// Compass convention: 0°=front, +90°=left, −90°=right, 180°=back.
const HEADING_RELATIVE_KEYS = [
  "HEADING_REL_FRONT",
  "HEADING_REL_FRONT_LEFT",
  "HEADING_REL_LEFT",
  "HEADING_REL_BACK_LEFT",
  "HEADING_REL_BACK",
  "HEADING_REL_BACK_RIGHT",
  "HEADING_REL_RIGHT",
  "HEADING_REL_FRONT_RIGHT",
] as const

export const getHeadingRelativeLabelKey = (heading: number): string => {
  // Normalise to [0, 360)
  const normalized = ((heading % 360) + 360) % 360
  const sector = Math.round(normalized / 45) % 8
  return `plots_descriptions.DEVICE_POSITION.${HEADING_RELATIVE_KEYS[sector]}`
}

export const getAnglesAmplitude = (
  data: EntityTelemetry[] | undefined,
  fieldMapping: PositionFieldMapping = defaultPositionFieldMapping,
) => ({
  first: fp.find({ _field: fieldMapping.first }, data),
  second: fp.find({ _field: fieldMapping.second }, data),
})
